<template>
    <div v-if="componentLoader" class="position-relative h-100">
        <app-overlay-loader/>
    </div>
    <div v-else>
        <app-overlay-loader v-if="preloader"/>
        <form class="mb-0" ref="form" v-else
              :class="{'loading-opacity': preloader}">
            <div class="form-group">
                <div class="row">
                    <div class="col-lg-3 col-xl-3 d-flex align-items-center">
                        <label for="user_old_password" class="text-left d-block mb-lg-2 mb-xl-0">
                            {{ $t('old_password') }}
                        </label>
                    </div>
                    <div class="col-lg-8 col-xl-8">
                        <app-input id="user_old_password"
                                   type="password"
                                   :placeholder="$t('type_your_current_password')"
                                   v-model="authUser.old_password"
                                   :error-message="$errorMessage(errors, 'old_password')"
                                   :required="true"/>
                    </div>
                </div>
            </div>
            <div class="form-group">
                <div class="row">
                    <div class="col-lg-3 col-xl-3">
                        <label for="user_password" class="text-left d-block mb-lg-2 mb-xl-0">
                            {{ $t('new_password') }}
                        </label>
                    </div>
                    <div class="col-lg-8 col-xl-8">
                        <app-input id="user_password"
                                   type="password"
                                   :placeholder="$t('type_your_new_password')"
                                   v-model="authUser.password"
                                   :specialValidation="true"
                                   :required="true"/>
                        <PasswordWarning/>
                    </div>
                </div>
            </div>
            <div class="form-group">
                <div class="row">
                    <div class="col-lg-3 col-xl-3 d-flex align-items-center">
                        <label for="user_password_confirmation" class="text-left d-block mb-lg-2 mb-xl-0">
                            {{ $t('conform_password') }}
                        </label>
                    </div>
                    <div class="col-lg-8 col-xl-8">
                        <app-input id="user_password_confirmation"
                                   type="password"
                                   :placeholder="$t('re_type_your_new_password')"
                                   v-model="authUser.password_confirmation"
                                   same-as="authUser_password"
                                   :required="true"/>
                    </div>
                </div>
            </div>
            <div class="form-group mb-0 mt-5">
                <div class="row">
                    <div class="col-12 action-buttons">
                        <button type="submit" class="btn btn-primary mr-3" @click.prevent="submit">
                            {{ $t('save') }}
                        </button>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
    import {FormMixin} from "../../../../core/mixins/form/FormMixin";
    import GlobalHelperMixin from "../../../Mixins/global/GlobalHelperMixin";
    import {UserMixin} from "./Mixins/UserMixin";
    import PasswordWarning from "./PasswordWarning";
    import * as actions from "../../../Config/ApiUrl";
    import {urlGenerator, externalApiCall} from "../../../Helpers/AxiosHelper";

    export default {
        name: "PasswordChange",
        mixins: [FormMixin, UserMixin, GlobalHelperMixin],
        components:{
            PasswordWarning
        },

        data() {
            return {
                preloader: false,
                authUser: {
                    old_password: '',
                    password: '',
                    password_confirmation: ''
                },
                errors: {}
            }
        },
        methods: {
            beforeSubmit() {
                this.scrollToTop(true);
                this.preloader = true;
                this.errors = {};
            },
            async submit() {
                try {
                    // Validate required fields
                    if (!this.authUser.old_password) {
                        this.errors = { old_password: ['Current password is required'] };
                        this.$toastr.e('Please enter your current password');
                        this.preloader = false;
                        return;
                    }
                    
                    if (!this.authUser.password) {
                        this.errors = { password: ['New password is required'] };
                        this.$toastr.e('Please enter a new password');
                        this.preloader = false;
                        return;
                    }
                    
                    if (this.authUser.password !== this.authUser.password_confirmation) {
                        this.errors = { password_confirmation: ['Passwords do not match'] };
                        this.$toastr.e('New passwords do not match');
                        this.preloader = false;
                        return;
                    }
                    
                    // Prepare data for external API (map field names)
                    const submitData = {
                        current_password: this.authUser.old_password,
                        new_password: this.authUser.password,
                        new_password_confirmation: this.authUser.password_confirmation
                    };
                    
                    // Call external API
                    const response = await externalApiCall('POST', actions.RESET_USER_PASSWORD, submitData);
                    
                    if (response.data && response.data.success) {
                        this.afterSuccess(response);
                    } else {
                        this.afterError(response);
                    }
                } catch (error) {
                    console.error('Password reset error:', error);
                    this.afterError(error.response || { data: { message: error.message || 'Failed to reset password' } });
                } finally {
                    this.preloader = false;
                }
            },
            afterFinalResponse() {
                this.preloader = false;
            },
            afterSuccess(res) {
                const message = res.data?.message || 'Password reset successfully';
                this.$toastr.s(message);
                
                // Clear form
                this.authUser = {
                    old_password: '',
                    password: '',
                    password_confirmation: ''
                };
                this.errors = {};
                
                // Redirect to logout after successful password change
                // (as per original behavior - user needs to login again with new password)
                setTimeout(() => {
                    window.location = urlGenerator(actions.LOGOUT);
                }, 1500);
            },
            afterError(res) {
                // Handle validation errors
                if (res.data?.errors) {
                    this.errors = res.data.errors;
                }
                
                const message = res.data?.message || res.message || 'Failed to reset password';
                this.$toastr.e(message);
            }
        }
    }
</script>
